"use client";

/**
 * Client body for the ticket-questions use-case page. Lives apart from
 * `page.tsx` (which stays a server component for `metadata`) so it can call
 * `useT()` and follow the visitor's locale, exactly like the homepage. All
 * copy comes from the `ucTicketQuestions` namespace; structure (icons, hrefs)
 * stays here.
 */

import {
  MessageSquare,
  Search,
  ShieldCheck,
  ListChecks,
  AlertTriangle,
  Lock,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  ProductShot,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function TicketQuestionsContent() {
  const t = useT();
  const k = (s: string) => `ucTicketQuestions.${s}`;

  const steps = [
    { key: "ask", icon: <MessageSquare size={18} /> },
    { key: "intent", icon: <Search size={18} /> },
    { key: "lookup", icon: <ListChecks size={18} /> },
    { key: "followup", icon: <ShieldCheck size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`steps.${s.key}.tag`)),
    title: t(k(`steps.${s.key}.title`)),
    body: t(k(`steps.${s.key}.body`)),
  }));

  const features = [
    { key: "status", icon: <ListChecks size={18} /> },
    { key: "noCreate", icon: <Search size={18} /> },
    { key: "partial", icon: <AlertTriangle size={18} /> },
    { key: "scope", icon: <Lock size={18} /> },
    { key: "permission", icon: <ShieldCheck size={18} /> },
    { key: "surfaces", icon: <MessageSquare size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/contact", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <ProductShot src="/screenshots/issues.png" alt={t(k("shot.alt"))} label={t(k("shot.label"))} />

      <Section>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("features.eyebrow"))}
          title={t(k("features.title"))}
          description={t(k("features.description"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<Lock size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t(k("related.approvedReplies.label")),
              description: t(k("related.approvedReplies.description")),
            },
            {
              href: "/integrations/slack",
              label: t(k("related.slack.label")),
              description: t(k("related.slack.description")),
            },
            {
              href: "/integrations/jira",
              label: t(k("related.jira.label")),
              description: t(k("related.jira.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
